import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  ChevronLeft, GraduationCap, MapPin, Clock, Banknote, 
  ExternalLink, Search, Filter, Building, BookOpen, AlertCircle
} from 'lucide-react';
import api from '../lib/api';

const LearnershipFinderPage = () => {
  const navigate = useNavigate();
  const [learnerships, setLearnerships] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [selectedProvince, setSelectedProvince] = useState('all');
  const [selectedSeta, setSelectedSeta] = useState('all');
  const [selectedNqf, setSelectedNqf] = useState('all');

  useEffect(() => {
    fetchLearnerships();
  }, [selectedIndustry, selectedProvince, selectedSeta, selectedNqf]);

  const fetchLearnerships = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedIndustry !== 'all') params.append('industry', selectedIndustry);
      if (selectedProvince !== 'all') params.append('province', selectedProvince);
      if (selectedSeta !== 'all') params.append('seta', selectedSeta);
      if (selectedNqf !== 'all') params.append('nqf_level', selectedNqf);

      const response = await api.get(`/learnerships?${params.toString()}`);
      setLearnerships(response.data.learnerships || []);
      setFilters(response.data.filters || {});
    } catch (error) {
      console.error('Error fetching learnerships:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredLearnerships = learnerships.filter(l => 
    l.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    l.seta.toLowerCase().includes(searchTerm.toLowerCase()) ||
    l.industry.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getNqfColor = (level) => {
    if (level <= 3) return 'bg-blue-100 text-blue-700';
    if (level <= 5) return 'bg-emerald-100 text-emerald-700';
    return 'bg-purple-100 text-purple-700';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 lg:px-8 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              <ChevronLeft className="w-5 h-5 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <GraduationCap className="w-6 h-6 text-amber-600" />
                Learnership & Internship Finder
              </h1>
              <p className="text-sm text-slate-500">Explore SETA-registered opportunities across South Africa</p>
            </div>
          </div>
          <Badge className="bg-amber-100 text-amber-700">{filteredLearnerships.length} Opportunities</Badge>
        </div>
      </header>

      {/* Info Banner */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="text-amber-800 font-medium">Get Paid While You Learn</p>
              <p className="text-amber-700 text-sm">Learnerships combine workplace experience with structured learning. You earn a stipend while gaining an NQF qualification.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search learnerships..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Industry" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Industries</SelectItem>
                  {filters.industries?.map((ind) => (
                    <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedProvince} onValueChange={setSelectedProvince}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Province" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Provinces</SelectItem>
                  {filters.provinces?.map((prov) => (
                    <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedSeta} onValueChange={setSelectedSeta}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="SETA" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All SETAs</SelectItem>
                  {filters.setas?.map((seta) => (
                    <SelectItem key={seta} value={seta}>{seta}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedNqf} onValueChange={setSelectedNqf}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="NQF Level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All NQF Levels</SelectItem>
                  {filters.nqf_levels?.map((level) => (
                    <SelectItem key={level} value={level.toString()}>NQF {level}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-600">Loading learnerships...</p>
          </div>
        ) : filteredLearnerships.length === 0 ? (
          <div className="text-center py-12">
            <GraduationCap className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No learnerships found matching your criteria</p>
            <Button variant="link" onClick={() => {
              setSearchTerm('');
              setSelectedIndustry('all');
              setSelectedProvince('all');
              setSelectedSeta('all');
              setSelectedNqf('all');
            }}>
              Clear all filters
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {filteredLearnerships.map((learnership) => (
              <Card key={learnership.id} className="hover:shadow-lg transition-shadow border-slate-200" data-testid={`learnership-${learnership.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <Badge className={getNqfColor(learnership.nqf_level)}>
                      NQF {learnership.nqf_level}
                    </Badge>
                    <Badge variant="outline" className="text-slate-600">
                      {learnership.seta}
                    </Badge>
                  </div>
                  <CardTitle className="text-lg mt-2">{learnership.title}</CardTitle>
                  <CardDescription>{learnership.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        {learnership.province}
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Clock className="w-4 h-4 text-slate-400" />
                        {learnership.duration}
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Banknote className="w-4 h-4 text-emerald-500" />
                        <span className="font-medium text-emerald-700">{learnership.stipend}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Building className="w-4 h-4 text-slate-400" />
                        {learnership.industry}
                      </div>
                    </div>

                    <div className="pt-3 border-t border-slate-100">
                      <p className="text-xs text-slate-500 mb-1">Requirements:</p>
                      <p className="text-sm text-slate-700">{learnership.requirements}</p>
                    </div>

                    {learnership.apply_note && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                        <p className="text-xs text-amber-800">
                          <strong>How to apply:</strong> {learnership.apply_note}
                        </p>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                      <div>
                        <p className="text-xs text-slate-500">Application Deadline</p>
                        <p className="text-sm font-medium text-slate-900">{learnership.deadline}</p>
                      </div>
                      <a
                        href={learnership.apply_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        View Details
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Info Section */}
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="pt-6">
              <BookOpen className="w-8 h-8 text-blue-600 mb-3" />
              <h3 className="font-semibold text-blue-900 mb-2">What is a Learnership?</h3>
              <p className="text-blue-800 text-sm">A structured programme combining theoretical learning at a college with practical workplace experience at a host employer.</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="pt-6">
              <Building className="w-8 h-8 text-purple-600 mb-3" />
              <h3 className="font-semibold text-purple-900 mb-2">Who Provides It?</h3>
              <p className="text-purple-800 text-sm"><strong>SETAs</strong> fund & accredit programmes. <strong>Companies</strong> host learners and pay stipends. <strong>Colleges</strong> deliver training.</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
            <CardContent className="pt-6">
              <GraduationCap className="w-8 h-8 text-emerald-600 mb-3" />
              <h3 className="font-semibold text-emerald-900 mb-2">NQF Qualification</h3>
              <p className="text-emerald-800 text-sm">Upon completion, you receive a nationally recognized qualification registered on the NQF - valued by all SA employers.</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="pt-6">
              <Banknote className="w-8 h-8 text-amber-600 mb-3" />
              <h3 className="font-semibold text-amber-900 mb-2">Who Pays the Stipend?</h3>
              <p className="text-amber-800 text-sm">The <strong>host employer/company</strong> pays your monthly stipend (R2,500 - R6,000). SETAs reimburse companies through grants.</p>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default LearnershipFinderPage;
